// Shared styles for action cards. Used by:
//   - <game-map> for the character-action menu cards in the actions modal
//     (_renderActionCard / .menu-item.rich).
//   - <rest-action> for prep-phase rest-action buttons (#381). Same visual
//     vocabulary; same component-level CSS.
//
// Components include this in their static styles array, e.g.
// `static styles = [actionCardStyles, css\`...component-specific...\`]`.

import { css } from 'lit';

export const actionCardStyles = css`
  .menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    background: transparent;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
    /* Grid in modal-content provides the gap between items. */
  }

  .menu-item:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.3);
    border-color: rgba(59, 130, 246, 0.5);
  }

  .menu-item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Rich cards: keep the card itself fully opaque (so the disabled-reason
     overlay can render at full opacity) and just gray the content. */
  .menu-item.rich.is-disabled {
    opacity: 1;
    cursor: not-allowed;
    color: rgba(255, 255, 255, 0.4);
  }

  .menu-item.rich.is-disabled .action-description,
  .menu-item.rich.is-disabled .action-summary {
    color: rgba(255, 255, 255, 0.35);
  }

  .menu-item.rich.is-disabled .action-icon,
  .menu-item.rich.is-disabled .bottom-row-uses {
    color: rgba(255, 255, 255, 0.35);
  }

  .menu-item.rich {
    flex-direction: column;
    align-items: stretch;
    /* Override .menu-item's space-between so column-flex content stays
       packed at the top instead of spreading apart when the card is
       taller than its content (e.g., when grid rows stretch). */
    justify-content: flex-start;
    gap: 6px;
    position: relative;
    height: 100%;
  }

  .menu-item.rich .action-disabled-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: calc(100% - 16px);
    width: max-content;
    padding: 6px 10px;
    background: rgba(0, 0, 0, 0.92);
    border: 1px solid white;
    color: white;
    font-size: 12px;
    line-height: 1.3;
    border-radius: 4px;
    white-space: normal;
    text-align: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease-in-out;
    z-index: 10;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .menu-item.rich .action-disabled-overlay-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    flex: 0 0 1em;
    color: white;
  }

  .menu-item.rich .action-disabled-overlay-icon svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  .menu-item.rich.is-disabled:hover .action-disabled-overlay {
    opacity: 1;
  }

  .menu-item.rich .action-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .menu-item.rich .action-icon {
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
    color: rgba(255, 255, 255, 0.85);
  }

  .menu-item.rich .action-icon svg {
    width: 100%;
    height: 100%;
  }

  .menu-item.rich .action-title {
    flex: 1;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .menu-item.rich .action-description {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
  }

  .menu-item.rich .action-disabled-icon,
  .menu-item.rich .action-info-icon {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    color: rgba(255, 255, 255, 0.7);
  }

  .menu-item.rich .action-info-icon {
    cursor: help;
  }

  .menu-item.rich .action-disabled-icon svg,
  .menu-item.rich .action-info-icon svg {
    width: 100%;
    height: 100%;
  }

  .menu-item.rich .bottom-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* "X/Y uses" / "X/Y slots" pushed to the right edge of the bottom row. */
  .menu-item.rich .bottom-row-uses {
    margin-left: auto;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.85);
  }

  .menu-item.rich .action-summary {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.85);
  }
`;
